<template>
  <div class="wrap">
    <myHead />
    <div class="wrap_body">
      <div class="center">
        <div class="ucenter_main">
          <ucMenu num="1" />
          <div class="right_con" v-loading="loading">
            <div class="page-header">
              <div class="page-header-title">
                <h2>{{ isGlobalShop ? '系统预设属性' : '搬家配置' }}</h2>
                <span v-if="shop.shop_name" class="page-header-shop">{{ shop.shop_name }}</span>
              </div>
              <el-button size="small" @click="$router.push('/ucenter')">返回店铺管理</el-button>
            </div>

            <div class="config-tabs">
              <div
                v-for="tab in configTabs"
                :key="tab.key"
                class="config-tab-item"
                :class="{ active: activeTab === tab.key }"
                @click="activeTab = tab.key"
              >{{ tab.label }}</div>
            </div>

            <div v-if="activeTab === 'attribute'" class="config-body">
              <section class="config-card attribute-card">
                <div class="card-header">
                  <h3 class="card-title">{{ isGlobalShop ? '系统预设属性' : '类目属性设置' }}</h3>
                  <div class="attribute-stats">
                    <span class="stat-badge">已设置 {{ activeConfiguredCount }} 个属性</span>
                    <span class="stat-badge">{{ isGlobalShop ? '有效叶子类目' : '已同步' }} {{ categoryCount }} 个类目</span>
                    <span v-if="!isGlobalShop" class="stat-badge">最近同步 {{ categorySyncedAtText }}</span>
                  </div>
                </div>
                <el-alert
                  v-if="shop.mer_shop_id && !productAppAvailable"
                  class="status-alert"
                  type="warning"
                  title="抖店商品应用未授权或已失效，请先返回店铺管理重新授权。"
                  show-icon
                  :closable="false"
                />
                <div class="attribute-actions">
                  <el-button
                    type="primary"
                    class="setting-button"
                    :disabled="(!isGlobalShop && !activeTemplateId) || categoryCount === 0"
                    @click="attributeDialogVisible = true"
                  >{{ isGlobalShop ? '配置系统预设属性' : '类目属性设置' }}</el-button>
                  <el-button
                    v-if="!isGlobalShop"
                    class="setting-button sync-category-button"
                    :loading="syncing"
                    :disabled="syncing || !productAppAvailable"
                    @click="startCategorySync"
                  >同步抖店类目</el-button>
                  <el-button
                    v-if="!isGlobalShop"
                    class="setting-button secondary-button"
                    :loading="clearingAttributes"
                    :disabled="!activeTemplateId || activeConfiguredCount === 0"
                    @click="clearTemplateAttributes"
                  >清空配置</el-button>
                  <el-button
                    v-if="!isGlobalShop"
                    class="setting-button secondary-button"
                    :loading="globalImporting"
                    :disabled="!activeTemplateId"
                    @click="importGlobalAttributes"
                  >同步系统预设属性</el-button>
                </div>
                <p class="sync-tip">
                  <i class="el-icon-info" />
                  {{ isGlobalShop
                    ? '这里配置的是系统预设属性，普通店铺需手动确认后覆盖到自己的模板。'
                    : '仅当当前显示的类目与抖店后台可经营类目不一致时，才需要同步。' }}
                </p>
              </section>
            </div>

            <div v-else class="tab-placeholder">
              <i class="el-icon-folder-opened" />
              <p>「{{ activeTabLabel }}」配置敬请期待</p>
            </div>

            <div v-if="!isGlobalShop" class="config-footer">
              <div class="footer-template-tip" role="note">
                <i class="el-icon-info" aria-hidden="true" />
                <span>铺货时将使用设置为【默认】模板中的配置</span>
              </div>
              <span class="footer-template-label">搬家配置模板</span>
              <el-popover
                v-model="templatePopoverVisible"
                placement="top-start"
                width="340"
                trigger="click"
                popper-class="move-template-popover"
              >
                <div class="template-popover-content">
                  <el-input
                    v-model.trim="templateKeyword"
                    class="template-search"
                    placeholder="请输入模板名称"
                    prefix-icon="el-icon-search"
                    clearable
                  />
                  <div class="template-option-list">
                    <div
                      v-for="item in filteredTemplates"
                      :key="item.id"
                      class="template-option"
                      :class="{ active: Number(item.id) === Number(activeTemplateId) }"
                      @click="selectTemplate(item)"
                    >
                      <span>{{ item.name }}</span>
                      <i
                        v-if="Number(item.id) === Number(activeTemplateId)"
                        class="el-icon-check"
                      />
                    </div>
                    <div v-if="filteredTemplates.length === 0" class="template-empty">
                      暂无匹配模板
                    </div>
                  </div>
                  <div class="template-popover-actions">
                    <el-button type="text" @click="createTemplateFromPopover">新建模板</el-button>
                    <el-button type="text" @click="openTemplateManage">管理模板</el-button>
                  </div>
                </div>
                <div slot="reference" class="template-select-trigger">
                  <span :class="{ placeholder: !activeTemplateId }">
                    {{ activeTemplate.name || '请选择模板' }}
                  </span>
                  <i
                    class="el-icon-arrow-down"
                    :class="{ open: templatePopoverVisible }"
                  />
                </div>
              </el-popover>
              <el-button
                type="primary"
                class="sync-multi-button"
                :loading="syncTargetsLoading"
                :disabled="!activeTemplateId || syncSaving"
                @click="openTemplateSyncDialog"
              >同步保存到多店</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <CategoryAttributeDialog
      :visible.sync="attributeDialogVisible"
      :shop-id="shopId"
      :template-id="activeTemplateId"
      :global-mode="isGlobalShop"
      @saved="loadConfig"
    />
    <el-dialog
      v-if="!isGlobalShop"
      title="管理搬家配置模板"
      :visible.sync="templateManageVisible"
      width="760px"
      :close-on-click-modal="false"
      class="template-manage-dialog"
    >
      <el-alert
        class="template-manage-tip"
        type="info"
        title="铺货时将使用默认模板中的配置，请将需要用于铺货的模板设为默认。"
        show-icon
        :closable="false"
      />
      <el-table
        :data="templateManageRows"
        border
        max-height="420"
        empty-text="暂无模板"
      >
        <el-table-column label="模板名称" min-width="380">
          <template slot-scope="scope">
            <div class="template-name-cell">
              <el-input
                v-model="scope.row.name"
                maxlength="50"
                show-word-limit
              />
              <span v-if="scope.row.is_default === 1" class="default-template-mark">默认</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="280">
          <template slot-scope="scope">
            <el-button
              type="text"
              :disabled="scope.row.is_default === 1"
              @click="setDefaultTemplate(scope.row)"
            >设为默认</el-button>
            <el-button type="text" @click="copyManagedTemplate(scope.row)">复制模板</el-button>
            <el-button
              type="text"
              :disabled="scope.row.is_default === 1 || templateManageRows.length <= 1"
              @click="deleteTemplate(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button :disabled="templateManageSaving" @click="templateManageVisible = false">关闭</el-button>
        <el-button
          type="primary"
          :loading="templateManageSaving"
          @click="saveManagedTemplateNames"
        >确定</el-button>
      </span>
    </el-dialog>
    <el-dialog
      v-if="!isGlobalShop"
      title="同步搬家配置到多店"
      :visible.sync="syncDialogVisible"
      width="640px"
      :close-on-click-modal="false"
    >
      <div class="sync-dialog-tip">
        请选择目标店铺及搬家配置模板。同步后，目标模板的整套搬家配置将被当前模板覆盖。
      </div>
      <el-table
        v-loading="syncTargetsLoading"
        :data="syncTargets"
        border
        max-height="420"
        empty-text="暂无其他抖店商品应用正常的店铺"
      >
        <el-table-column label="选择" width="70" align="center">
          <template slot-scope="scope">
            <el-checkbox v-model="scope.row.selected" />
          </template>
        </el-table-column>
        <el-table-column prop="shop_name" label="店铺" min-width="190" />
        <el-table-column label="搬家配置模板" min-width="260">
          <template slot-scope="scope">
            <el-select
              v-model="scope.row.target_template_id"
              :disabled="!scope.row.selected"
              placeholder="请选择模板"
              class="sync-template-select"
            >
              <el-option
                v-for="template in scope.row.templates"
                :key="template.id"
                :label="template.name"
                :value="template.id"
              />
            </el-select>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button :disabled="syncSaving" @click="syncDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="syncSaving" @click="syncTemplateToShops">保存</el-button>
      </span>
    </el-dialog>
    <myFoot />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import myHead from '@/components/myHead/myhead'
import myFoot from '@/components/myFoot/myfoot'
import ucMenu from '@/components/ucenterMenu/ucmenu'
import CategoryAttributeDialog from './components/CategoryAttributeDialog.vue'

export default {
  name: 'DoudianMoveConfig',
  components: { myHead, myFoot, ucMenu, CategoryAttributeDialog },
  data() {
    return {
      loading: false,
      activeTab: 'attribute',
      configTabs: [
        { key: 'attribute', label: '属性设置' }
      ],
      shopId: 0,
      shop: {},
      templates: [],
      activeTemplateId: '',
      categoryCount: 0,
      categorySyncedAt: 0,
      syncTask: null,
      syncTimer: null,
      templatePopoverVisible: false,
      templateKeyword: '',
      templateManageVisible: false,
      templateManageSaving: false,
      templateManageRows: [],
      attributeDialogVisible: false,
      clearingAttributes: false,
      syncTargetsLoading: false,
      syncSaving: false,
      syncDialogVisible: false,
      syncTargets: [],
      isGlobalShop: false,
      globalConfiguredAttributeCount: 0,
      globalImporting: false
    }
  },
  computed: {
    activeTabLabel() {
      const tab = this.configTabs.find(item => item.key === this.activeTab)
      return tab ? tab.label : ''
    },
    activeTemplate() {
      return this.templates.find(item => Number(item.id) === Number(this.activeTemplateId)) || {}
    },
    filteredTemplates() {
      const keyword = String(this.templateKeyword || '').trim().toLowerCase()
      if (!keyword) return this.templates
      return this.templates.filter(item => String(item.name || '').toLowerCase().includes(keyword))
    },
    activeConfiguredCount() {
      if (this.isGlobalShop) return this.globalConfiguredAttributeCount
      return Number(this.activeTemplate.configured_count || 0)
    },
    syncing() {
      return this.syncTask && ['pending', 'running'].includes(this.syncTask.status)
    },
    productAppAvailable() {
      return Number(this.shop.app_product_status || 0) === 1
    },
    categorySyncedAtText() {
      const timestamp = Number(this.categorySyncedAt || 0)
      if (!timestamp) return '暂未同步'
      const date = new Date(timestamp * 1000)
      const pad = value => String(value).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    }
  },
  created() {
    this.shopId = Number(this.$route.query.shop_id || 0)
    if (!this.shopId) {
      this.$message.error('缺少店铺ID')
      this.$router.replace('/ucenter')
      return
    }
    this.loadConfig()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    ...mapActions({
      getConfig: 'ucenter/getDoudianMoveConfig',
      createTemplateAction: 'ucenter/createDoudianMoveTemplate',
      renameTemplateAction: 'ucenter/renameDoudianMoveTemplate',
      setDefaultTemplateAction: 'ucenter/setDefaultDoudianMoveTemplate',
      deleteTemplateAction: 'ucenter/deleteDoudianMoveTemplate',
      clearTemplateCategoryConfigs: 'ucenter/clearDoudianMoveTemplateCategoryConfigs',
      syncCategories: 'ucenter/syncDoudianMoveCategories',
      getSyncTask: 'ucenter/getDoudianMoveCategorySyncTask',
      getTemplateSyncTargets: 'ucenter/getDoudianMoveTemplateSyncTargets',
      syncTemplate: 'ucenter/syncDoudianMoveTemplate',
      getGlobalConfigPreview: 'ucenter/getDoudianMoveGlobalConfigPreview',
      importGlobalConfig: 'ucenter/importDoudianMoveGlobalConfig'
    }),
    loadConfig(keepActive = true) {
      this.loading = true
      const previousId = keepActive ? Number(this.activeTemplateId) : 0
      this.getConfig({
        shopId: this.shopId,
        success: res => {
          this.loading = false
          if (res.code !== 200) {
            this.$message.error(res.msg || '获取搬家配置失败')
            return
          }
          const data = res.data || {}
          this.shop = data.shop || {}
          this.templates = data.templates || []
          this.categoryCount = Number(data.category_count || 0)
          this.categorySyncedAt = Number(data.category_synced_at || 0)
          this.syncTask = data.sync_task || null
          this.isGlobalShop = Boolean(data.is_global_attribute_shop)
          this.globalConfiguredAttributeCount = Number(data.configured_attribute_count || 0)
          const activeExists = this.templates.some(item => Number(item.id) === previousId)
          this.activeTemplateId = activeExists ? previousId : Number(data.default_template_id || (this.templates[0] || {}).id || 0)
          if (this.templateManageVisible) this.refreshTemplateManageRows()
          if (this.syncing) this.startPolling()
        }
      })
    },
    refreshTemplateManageRows() {
      this.templateManageRows = this.templates.map(item => ({
        ...item,
        original_name: item.name
      }))
    },
    selectTemplate(template) {
      this.activeTemplateId = Number(template.id)
      this.templatePopoverVisible = false
      this.templateKeyword = ''
    },
    createTemplateFromPopover() {
      this.templatePopoverVisible = false
      this.templateKeyword = ''
      this.createTemplate(false)
    },
    openTemplateManage() {
      this.templatePopoverVisible = false
      this.templateKeyword = ''
      this.refreshTemplateManageRows()
      this.templateManageVisible = true
    },
    copyManagedTemplate(template) {
      this.createTemplate(true, template)
    },
    saveManagedTemplateNames() {
      const normalizedRows = this.templateManageRows.map(item => ({
        ...item,
        name: String(item.name || '').trim()
      }))
      const invalidRow = normalizedRows.find(item => item.name.length === 0 || item.name.length > 50)
      if (invalidRow) return this.$message.error('模板名称长度应为1至50个字符')
      const names = normalizedRows.map(item => item.name)
      if (new Set(names).size !== names.length) return this.$message.error('模板名称不能重复')

      const changedRows = normalizedRows.filter(item => item.name !== item.original_name)
      if (changedRows.length === 0) {
        this.templateManageVisible = false
        return
      }

      this.templateManageSaving = true
      const saveNext = index => {
        if (index >= changedRows.length) {
          this.templateManageSaving = false
          this.templateManageVisible = false
          this.$message.success('模板名称已更新')
          this.loadConfig()
          return
        }
        const item = changedRows[index]
        this.renameTemplateAction({
          templateId: item.id,
          data: { name: item.name },
          success: res => {
            if (res.code !== 200) {
              this.templateManageSaving = false
              this.$message.error(res.msg || '重命名失败')
              return
            }
            saveNext(index + 1)
          }
        })
      }
      saveNext(0)
    },
    startCategorySync() {
      if (!this.productAppAvailable) {
        this.$message.error('抖店商品应用未授权或已失效，请先返回店铺管理重新授权')
        return
      }
      this.$confirm(
        '同步后将以抖店后台当前可经营类目更新本地类目库。仅在当前显示类目与抖店后台不一致时执行，是否继续？',
        '同步抖店类目',
        {
          type: 'warning',
          confirmButtonText: '继续同步',
          cancelButtonText: '取消'
        }
      ).then(() => {
        this.syncCategories({
          shopId: this.shopId,
          success: res => {
            if (res.code !== 200) {
              this.$message.error(res.msg || '提交同步任务失败')
              return
            }
            this.syncTask = res.data
            this.$message.success(res.msg || '类目同步任务已提交')
            this.startPolling()
          }
        })
      }).catch(() => {})
    },
    startPolling() {
      this.stopPolling()
      this.syncTimer = setInterval(this.pollSyncTask, 2000)
      this.pollSyncTask()
    },
    stopPolling() {
      if (this.syncTimer) clearInterval(this.syncTimer)
      this.syncTimer = null
    },
    pollSyncTask() {
      if (!this.syncTask || !this.syncTask.id) return
      this.getSyncTask({
        taskId: this.syncTask.id,
        success: res => {
          if (res.code !== 200) return
          this.syncTask = res.data
          if (!['pending', 'running'].includes(this.syncTask.status)) {
            this.stopPolling()
            if (this.syncTask.status === 'success') {
              this.$message.success('抖店类目同步完成')
              this.loadConfig()
            } else {
              this.$message.error(this.syncTask.error_message || '抖店类目同步失败')
            }
          }
        }
      })
    },
    createTemplate(copyCurrent, sourceTemplate = this.activeTemplate) {
      const title = copyCurrent ? '复制模板' : '新建模板'
      const defaultName = copyCurrent ? `${sourceTemplate.name || '配置'}-副本` : ''
      this.$prompt('请输入模板名称', title, {
        inputValue: defaultName,
        inputValidator: value => {
          const name = String(value || '').trim()
          return name.length > 0 && name.length <= 50 ? true : '模板名称长度应为1至50个字符'
        }
      }).then(({ value }) => {
        this.createTemplateAction({
          shopId: this.shopId,
          data: {
            name: String(value).trim(),
            copy_from_template_id: copyCurrent ? Number(sourceTemplate.id || 0) : 0
          },
          success: res => {
            if (res.code !== 200) {
              this.$message.error(res.msg || '模板创建失败')
              return
            }
            this.activeTemplateId = Number(res.data.id)
            this.$message.success(res.msg || '模板创建成功')
            this.loadConfig()
          }
        })
      }).catch(() => {})
    },
    setDefaultTemplate(template = this.activeTemplate) {
      this.$confirm(`确认将“${template.name}”设为默认模板？`, '提示', { type: 'warning' })
        .then(() => {
          this.setDefaultTemplateAction({
            templateId: template.id,
            success: res => {
              if (res.code !== 200) return this.$message.error(res.msg || '设置失败')
              this.$message.success(res.msg || '默认模板已更新')
              this.loadConfig()
            }
          })
        }).catch(() => {})
    },
    deleteTemplate(template = this.activeTemplate) {
      this.$confirm(`删除模板“${template.name}”后，其类目属性配置也会删除。是否继续？`, '提示', { type: 'warning' })
        .then(() => {
          this.deleteTemplateAction({
            templateId: template.id,
            success: res => {
              if (res.code !== 200) return this.$message.error(res.msg || '删除失败')
              const deletingActive = Number(template.id) === Number(this.activeTemplateId)
              if (deletingActive) this.activeTemplateId = ''
              this.$message.success(res.msg || '模板已删除')
              this.loadConfig(!deletingActive)
            }
          })
        }).catch(() => {})
    },
    clearTemplateAttributes() {
      this.$confirm(
        `确认清空模板“${this.activeTemplate.name}”的全部类目属性配置？清空后无法恢复。`,
        '清空配置',
        {
          type: 'warning',
          confirmButtonText: '确认清空',
          cancelButtonText: '取消'
        }
      ).then(() => {
        this.clearingAttributes = true
        this.clearTemplateCategoryConfigs({
          templateId: this.activeTemplateId,
          success: res => {
            this.clearingAttributes = false
            if (res.code !== 200) return this.$message.error(res.msg || '清空配置失败')
            this.$message.success(res.msg || '配置已清空')
            this.loadConfig()
          }
        })
      }).catch(() => {})
    },
    importGlobalAttributes() {
      this.globalImporting = true
      this.getGlobalConfigPreview({
        templateId: this.activeTemplateId,
        success: res => {
          this.globalImporting = false
          if (res.code !== 200) return this.$message.error(res.msg || '获取系统预设属性预览失败')
          const data = res.data || {}
          if (!Number(data.importable_count || 0)) {
            return this.$message.warning('当前店铺没有可同步的系统预设属性')
          }
          const message = `同步系统预设属性会覆盖当前模板已配置的类目属性。系统已配置 ${data.global_config_count || 0} 个类目，当前店铺可同步 ${data.importable_count || 0} 个，并将清除 ${data.will_clear_count || 0} 个不在同步范围内的类目配置。是否继续？`
          this.$confirm(message, '二次确认', {
            type: 'warning',
            confirmButtonText: '确认同步',
            cancelButtonText: '取消'
          }).then(() => this.executeGlobalImport()).catch(() => {})
        },
        fail: () => {
          this.globalImporting = false
          this.$message.error('获取系统预设属性预览失败，请稍后重试')
        }
      })
    },
    executeGlobalImport() {
      this.globalImporting = true
      this.importGlobalConfig({
        templateId: this.activeTemplateId,
        data: { confirmed: true },
        success: res => {
          this.globalImporting = false
          if (res.code !== 200) return this.$message.error(res.msg || '同步系统预设属性失败')
          this.$message.success(res.msg || '系统预设属性已同步到当前模板')
          this.loadConfig()
        },
        fail: () => {
          this.globalImporting = false
          this.$message.error('同步系统预设属性失败，请稍后重试')
        }
      })
    },
    openTemplateSyncDialog() {
      this.syncDialogVisible = true
      this.syncTargetsLoading = true
      this.syncTargets = []
      this.getTemplateSyncTargets({
        templateId: this.activeTemplateId,
        success: res => {
          this.syncTargetsLoading = false
          if (res.code !== 200) return this.$message.error(res.msg || '获取目标店铺失败')
          this.syncTargets = (res.data || []).map(shop => {
            const templates = shop.templates || []
            const defaultTemplate = templates.find(template => Number(template.is_default) === 1)
            return {
              ...shop,
              selected: false,
              target_template_id: Number((defaultTemplate || templates[0] || {}).id || 0)
            }
          })
        },
        fail: () => {
          this.syncTargetsLoading = false
          this.$message.error('获取目标店铺失败，请稍后重试')
        }
      })
    },
    syncTemplateToShops() {
      const selected = this.syncTargets.filter(shop => shop.selected)
      if (selected.length === 0) return this.$message.error('请选择需要同步的店铺')
      if (selected.some(shop => !shop.target_template_id)) {
        return this.$message.error('请选择目标店铺的搬家配置模板')
      }
      this.$confirm(
        `确认将“${this.activeTemplate.name}”的整套搬家配置覆盖到选中的 ${selected.length} 个店铺模板？`,
        '同步搬家配置',
        {
          type: 'warning',
          confirmButtonText: '确认同步',
          cancelButtonText: '取消'
        }
      ).then(() => {
        this.syncSaving = true
        this.syncTemplate({
          templateId: this.activeTemplateId,
          data: {
            target_template_ids: selected.map(shop => shop.target_template_id)
          },
          success: res => {
            this.syncSaving = false
            if (res.code !== 200) return this.$message.error(res.msg || '同步失败')
            this.syncDialogVisible = false
            this.$message.success(res.msg || '搬家配置同步成功')
          },
          fail: () => {
            this.syncSaving = false
            this.$message.error('同步失败，请稍后重试')
          }
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.ucenter_main {
  display: flex;
  min-height: 650px;
}

.right_con {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 0;
  background: #f5f7fa;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 72px;
  padding: 0 32px;
  border-bottom: 1px solid #ebeef5;
  background: #fff;
}

.page-header-title {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
}

.page-header h2 {
  margin: 0;
  color: #303133;
  font-size: 20px;
  font-weight: 500;
}

.page-header-shop {
  overflow: hidden;
  color: #909399;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.config-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 32px;
  border-bottom: 1px solid #ebeef5;
  background: #fff;
}

.config-tab-item {
  padding: 8px 22px;
  border-radius: 4px;
  color: #606266;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s, background 0.2s;
}

.config-tab-item:hover {
  color: #409eff;
}

.config-tab-item.active,
.config-tab-item.active:hover {
  background: #409eff;
  color: #fff;
}

.tab-placeholder {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  margin: 20px 24px 32px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
  color: #909399;
}

.tab-placeholder i {
  margin-bottom: 12px;
  color: #c0c4cc;
  font-size: 40px;
}

.tab-placeholder p {
  margin: 0;
  font-size: 14px;
}

.config-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: auto;
  padding: 14px 32px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}

.footer-template-label {
  color: #606266;
  font-size: 14px;
  white-space: nowrap;
}

.footer-template-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: auto;
  color: #606266;
  font-size: 13px;
  line-height: 20px;
}

.footer-template-tip i {
  color: #409eff;
  font-size: 14px;
}

.config-footer .template-select-trigger {
  width: 240px;
  height: 36px;
}

.config-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px 32px;
}

.config-card {
  padding: 20px 24px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.card-title {
  margin: 0;
  color: #303133;
  font-size: 16px;
  font-weight: 500;
}

.template-select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 300px;
  height: 40px;
  padding: 0 14px;
  box-sizing: border-box;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #303133;
  font-size: 14px;
  cursor: pointer;
}

.template-select-trigger:hover {
  border-color: #c0c4cc;
}

.template-select-trigger .placeholder {
  color: #c0c4cc;
}

.template-select-trigger i {
  color: #c0c4cc;
  transition: transform 0.2s;
}

.template-select-trigger i.open {
  transform: rotate(180deg);
}

.sync-multi-button {
  min-width: 140px;
  height: 36px;
  font-size: 14px;
}

.attribute-stats {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.stat-badge {
  padding: 4px 10px;
  border-radius: 12px;
  background: #f0f7ff;
  color: #409eff;
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;
}

.status-alert,
.template-manage-tip {
  margin-bottom: 16px;
}

.attribute-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.attribute-actions .el-button + .el-button {
  margin-left: 0;
}

.setting-button {
  min-width: 124px;
  height: 38px;
  padding: 0 20px;
  font-size: 14px;
}

.sync-category-button {
  border-color: #b3d8ff;
  background: #ecf5ff;
  color: #409eff;
}

.sync-category-button:hover,
.sync-category-button:focus {
  border-color: #409eff;
  background: #409eff;
  color: #fff;
}

.secondary-button {
  color: #303133;
}

.sync-tip {
  display: flex;
  align-items: center;
  margin: 14px 0 0;
  color: #909399;
  font-size: 13px;
  line-height: 20px;
}

.sync-tip i {
  margin-right: 4px;
  color: #409eff;
}

.sync-dialog-tip {
  margin-bottom: 14px;
  color: #909399;
  font-size: 13px;
  line-height: 20px;
}

.sync-template-select {
  width: 100%;
}

.template-name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.template-name-cell .el-input {
  flex: 1;
}

.default-template-mark {
  flex: 0 0 auto;
  color: #67c23a;
  font-size: 13px;
}

.template-popover-content {
  margin: -2px;
}

.template-search {
  margin-bottom: 10px;
}

.template-option-list {
  max-height: 240px;
  overflow-y: auto;
}

.template-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  padding: 0 10px;
  border-radius: 3px;
  color: #303133;
  font-size: 14px;
  cursor: pointer;
}

.template-option:hover {
  background: #f5f7fa;
}

.template-option.active {
  color: #409eff;
}

.template-empty {
  padding: 28px 0;
  color: #c0c4cc;
  font-size: 13px;
  text-align: center;
}

.template-popover-actions {
  display: flex;
  align-items: center;
  gap: 22px;
  margin: 8px -10px -10px;
  padding: 10px 12px 0;
  border-top: 1px solid #ebeef5;
}

.template-popover-actions .el-button + .el-button {
  margin-left: 0;
}

@media (max-width: 1200px) {
  .page-header {
    padding: 0 24px;
  }

  .config-tabs {
    padding: 12px 24px;
  }

  .config-body {
    padding: 16px 20px 28px;
  }

  .tab-placeholder {
    margin: 16px 20px 28px;
  }

  .config-footer {
    padding: 14px 24px;
  }
}

@media (max-width: 900px) {
  .page-header {
    padding: 14px 20px;
  }

  .config-tabs {
    padding: 10px 20px;
  }

  .config-tab-item {
    padding: 6px 14px;
  }

  .config-body {
    padding: 12px 16px 24px;
  }

  .tab-placeholder {
    min-height: 240px;
    margin: 12px 16px 24px;
  }

  .config-card {
    padding: 16px;
  }

  .card-header {
    margin-bottom: 14px;
  }

  .config-footer {
    padding: 12px 20px;
  }
}
</style>
